import BoardCardNew from "@/components/PinDetail/BoardCardNew";
import classNames from "classnames";
import React, { useCallback, useMemo, useRef, useState } from "react";
import styles from "./index.module.less";

interface SameBoardModuleProps {
  boards: any[];
  pinId?: string | number;
  style?: React.CSSProperties;
  className?: string;
  onLoadMore?: () => Promise<any[]>; // 新增：加载更多数据的回调函数
  hasMore?: boolean; // 新增：是否还有更多数据
  loading?: boolean; // 新增：是否正在加载
  searchWord?: string;
  stackIndex?: number; // 新增：弹窗堆栈索引，用于生成唯一key
}

// 计算单个BoardCardNew组件的高度
// 根据BoardCardNew/index.module.less的样式计算：
// 图片区域：163px + 标题区域：8px (margin-top) + 14px * 1.3 (line-height 130%) + 信息区域：4px (margin-top) + 18px
const BOARD_CARD_HEIGHT = 163 + 8 + Math.ceil(14 * 1.3) + 4 + 18; // ≈ 211px

/**
 * 同在画板模组组件
 * 规则：
 * 1. 宽度占用瀑布流一列，画板纵向排列
 * 2. 高度：≤5个时展示对应画板高度之和，>5个时可上下滚动，<3个不展示
 * 3. 使用原有的BoardCardNew组件
 */
function SameBoardModule({
  boards,
  pinId,
  style,
  className,
  onLoadMore,
  hasMore = false,
  loading = false,
  searchWord,
  stackIndex = 0,
}: SameBoardModuleProps) {
  // 滚动容器引用
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 过滤并限制画板数量（最少3个才展示）
  const validBoards = useMemo(() => {
    if (!boards || boards.length < 3) {
      return null; // 少于3个不展示
    }
    return boards.filter((board) => board && board.board_id);
  }, [boards]);

  // 计算模组高度 - 统一使用3个画板的高度
  const moduleHeight = useMemo(() => {
    if (!validBoards) return 0;

    const HEADER_HEIGHT = 32 + 8; // 标题高度32px + margin-bottom 8px
    const CONTAINER_PADDING = 8 * 2; // 容器上下padding各8px
    const BOARD_GAP = 16; // 画板之间的间距
    const FIXED_DISPLAY_COUNT = 3; // 固定显示3个画板的高度

    // 计算3个画板之间的gap总高度（3个画板有2个gap）
    const gapHeight = (FIXED_DISPLAY_COUNT - 1) * BOARD_GAP;

    // 统一使用3个画板的高度
    return (
      FIXED_DISPLAY_COUNT * BOARD_CARD_HEIGHT +
      HEADER_HEIGHT +
      CONTAINER_PADDING +
      gapHeight
    );
  }, [validBoards]);

  // 滚动到底部加载更多
  const handleScroll = useCallback(
    async (e: React.UIEvent<HTMLDivElement>) => {
      if (!onLoadMore || !hasMore || isLoadingMore) return;

      const target = e.target as HTMLDivElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      // 滚动到底部时加载更多（提前20px触发）
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        setIsLoadingMore(true);
        try {
          await onLoadMore();
        } catch (error) {
          console.error("加载更多画板失败:", error);
        } finally {
          setIsLoadingMore(false);
        }
      }
    },
    [onLoadMore, hasMore, isLoadingMore]
  );

  // 如果画板数量不足，不渲染
  if (!validBoards) {
    return null;
  }

  const boardCount = validBoards.length;
  const needScroll = boardCount > 3; // 多于3个画板时启用滚动

  return (
    <div
      className={classNames(styles.sameBoardModule, className)}
      style={{
        height: moduleHeight,
        ...style,
      }}
    >
      <div className={styles.header}>
        <h3 className={styles.title}>同在画板</h3>
      </div>

      <div
        ref={scrollRef}
        className={classNames(styles.boardList, {
          [styles.scrollable]: needScroll,
        })}
        style={{
          height: needScroll ? moduleHeight - 40 : "auto", // 减去header高度（32px + 8px margin-bottom），滚动区域固定为3个画板的高度
        }}
        onScroll={needScroll ? handleScroll : undefined}
      >
        {validBoards.map((board, index) => (
          <div
            key={`${stackIndex}-${board.board_id || index}`}
            className={styles.boardItem}
          >
            <BoardCardNew
              record={board}
              hideButton={true}
              hideUser={true}
              location={index + 1}
              viewSource={index === 0 ? pinId : undefined}
              disableHoverMask={false}
              searchWord={searchWord}
              stackIndex={stackIndex}
            />
          </div>
        ))}

        {/* 加载更多状态 */}
        {needScroll && hasMore && (isLoadingMore || loading) && (
          <div className={styles.loadingMore}>
            <div className={styles.loadingDot}>加载中...</div>
          </div>
        )}

        {/* 没有更多数据提示 */}
        {needScroll && !hasMore && boardCount > 3 && (
          <div className={styles.noMore}>
            <div className={styles.noMoreText}>没有更多了</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(SameBoardModule);
